/**
 * CR-1154: Hyperlink to a GRN (delivery) record.
 *
 * GRN detail is a modal on the Deliveries tab with no id-based deep-link, so
 * this targets the Deliveries tab filtered to the GRN number — the same
 * affordance the PO-line GRN tab already uses.
 *
 * Degrades to plain text when there is no GRN number, so callers can render
 * this unconditionally.
 *
 * @param grnNumber - The GRN number. Empty/null renders children as plain text.
 * @param children - The label to render (defaults to the GRN number).
 * @param className - Optional override for the link styling.
 * @returns A router link to the GRN on the Deliveries tab, or the bare children.
 *
 * @example
 * <GrnLink grnNumber={grn.grn_number} />
 */
import { Link } from 'react-router-dom';
import { ReactNode } from 'react';

interface GrnLinkProps {
  grnNumber?: string | null;
  children?: ReactNode;
  className?: string;
}

/** Canonical deep-link for a GRN on the Deliveries tab. */
export function grnHref(grnNumber: string): string {
  return `/purchasing?tab=delivery&search=${encodeURIComponent(grnNumber)}`;
}

export default function GrnLink({
  grnNumber,
  children,
  className = 'text-primary hover:underline',
}: GrnLinkProps) {
  const label = children ?? grnNumber ?? '-';
  if (!grnNumber) {
    return <>{label}</>;
  }
  // CR-1154: open in a new tab, consistent with PoLineLink / PoHeaderLink.
  return (
    <Link to={grnHref(grnNumber)} target="_blank" rel="noopener noreferrer" className={className}>
      {label}
    </Link>
  );
}
